import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  useTheme,
  Divider,
  Typography,
  Box,
} from '@ublend-npm/aulaui-core';
import { toggleStarSpace } from '@core/spaceSelector/operations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { getHomeSpacesLoading } from '@app/selectors/home';

// Imports specific to the Recent Spaces Widget
import { getHomeRecentSpaces } from './getHomeRecentSpaces';
import SpaceItem from '../common/SpaceItem';
import { WidgetCard } from '../common';
import { getStarredSpaceColors } from '../../colors';
import SpaceListSkeleton from '../common/SpaceListSkeleton'; // Reusing the skeleton

const STRINGS = {
  emptyState: 'No recently viewed spaces.',
  title: 'Recently viewed spaces',
  hide: 'Hide',
  viewAll: 'View all recently viewed spaces',
  widgetKey: 'home-recently-viewed-spaces-widget',
};

const ITEM_RENDER_COUNT = 5;
const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const RecentlyViewedSpacesWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();

  const isSpacesLoading = useSelector(getHomeSpacesLoading);
  const [showSkeleton, setShowSkeleton] = React.useState(true);

  React.useEffect(() => {
    if (isSpacesLoading) {
      setShowSkeleton(true);
      return undefined;
    }

    // Artificial delay to show skeleton
    const timer = setTimeout(() => setShowSkeleton(false), 2000);
    return () => clearTimeout(timer);
  }, [isSpacesLoading]);

  const isLoading = showSkeleton;
  const rawSpaces = useSelector(getHomeRecentSpaces) || [];

  const spaces = useMemo(() => {
    return rawSpaces.map((s: any) => ({
      id: s.objectId || s.id,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      isStarred: s.isStarred || false,
    }));
  }, [rawSpaces]);

  const handleHideWidget = useCallback(() => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
  }, [dispatch]);

  const handleStarToggle = useCallback(
    (id: string) => {
      dispatch(toggleStarSpace({ spaceId: id }));
    },
    [dispatch]
  );

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <SpaceListSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (spaces.length === 0) {
      // Inline empty state similar to StarredSpacesEmptyState but simpler since no zendesk link needed
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            {STRINGS.emptyState}
          </Typography>
        </Box>
      );
    }

    const visibleSpaces = spaces.slice(0, ITEM_RENDER_COUNT);

    return visibleSpaces.map((space) => (
      <React.Fragment key={space.id}>
        <SpaceItem
          space={space}
          isStarred={space.isStarred}
          onStarToggle={handleStarToggle}
        />
        <Divider />
      </React.Fragment>
    ));
  }, [isLoading, spaces, handleStarToggle]);

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const footer = (
    <Button
      fullWidth
      kind="secondary"
      endIcon={<ArrowForwardIcon />}
      sx={{
        width: '100%',
        color: getStarredSpaceColors(palette).starIcon,
      }}
      onClick={() => {
        /* TODO: Implement navigation */
      }}
    >
      {STRINGS.viewAll}
    </Button>
  );

  const footerUI = !isLoading && spaces.length > 0 ? footer : null;

  return (
    <WidgetCard
      title={STRINGS.title}
      menuActions={menuActions}
      footer={footerUI}
    >
      {content}
    </WidgetCard>
  );
};

export default RecentlyViewedSpacesWidget;
